import moment from 'moment';
import { IProvider } from 'model/v2';
import { IWeeklySessionsData } from 'model/v2/providerCapacity';

/** Map JS Date.getDay() (0=Sun) to the IWeeklySessionsData keys. */
const WEEKDAY_KEYS = [
  'sun',
  'mon',
  'tues',
  'wed',
  'thurs',
  'fri',
  'sat'
] as const;

interface ColumnHeaderProps {
  date: Date;
  label?: string;
  provider?: IProvider;
  showSessions?: boolean;
  /** Care sessions defined on the provider's template, per weekday. */
  templateSessions?: IWeeklySessionsData | null;
  /**
   * DT recurring template. It shows one standing week rather than a particular
   * one, so the column headers carry the weekday alone — a date number implies a
   * specific week and invites the reader to treat the template as a diary.
   */
  isTemplate?: boolean;
}

/**
 * Day-column header for the provider schedule calendar.
 *
 * The session pill reads booked over the care sessions the template defines for
 * that day — how many slots there are to fill, not a limit. Daily limits were
 * removed (AXON-3559); weekly is the only limit. Colour follows the same rule as
 * the planner badge and the sidebar bars: short of the template count is red,
 * every slot filled is green.
 */
export const ProviderColumnHeader = ({
  date,
  provider,
  showSessions,
  templateSessions,
  isTemplate
}: ColumnHeaderProps) => {
  const m = moment(date);
  const isToday = m.isSame(moment(), 'day');
  const key = WEEKDAY_KEYS[m.day()];
  const capacity = provider?.providerCapacity;
  const booked = capacity?.providerBookedSessions?.[key];
  // The DT template reports every weekday alike: a series booked on a Saturday
  // occupies a slot like any other, so hiding the pill there loses a real
  // booking. Other departments keep the previous behaviour, where Saturday
  // never carries a pill.
  const hideSaturday = !isTemplate && m.day() === 6;
  const hasSessions = showSessions && booked !== undefined && !hideSaturday;

  const bookedCount = booked ?? 0;
  const templateCount = templateSessions?.[key] ?? 0;
  // With no template count there is no denominator to show, so fall back to the
  // booked count alone rather than rendering "3/0".
  const hasTemplateCount = templateCount > 0;
  const status = !hasTemplateCount
    ? 'none'
    : bookedCount >= templateCount
    ? 'met'
    : 'under';

  return (
    <div className="provider-col-header">
      <div className="dow">{m.format('ddd').toUpperCase()}</div>
      {!isTemplate && (
        <div className={`dom ${isToday ? 'today' : ''}`}>{m.format('D')}</div>
      )}
      {hasSessions && (
        <div className={`sess ${status}`}>
          {hasTemplateCount
            ? `${bookedCount}/${templateCount} Sessions`
            : `${bookedCount} ${bookedCount === 1 ? 'Session' : 'Sessions'}`}
        </div>
      )}
    </div>
  );
};
